import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { MUSIC, PLATFORMS, LISTEN, BRAND } from "../data/content";
import { SectionLabel, Reveal } from "./Reveal";
import BrandIcon from "./BrandIcons";

function TiltCard({ release, onOpen, index }) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: -py * 10, ry: px * 12 });
  };
  const reset = () => setT({ rx: 0, ry: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      style={{ perspective: 1200 }}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        onClick={() => onOpen(release)}
        data-testid={`music-card-${release.id}`}
        data-cursor
        className="relative cursor-pointer transition-transform duration-200 will-change-transform"
        style={{ transform: `rotateX(${t.rx}deg) rotateY(${t.ry}deg)`, transformStyle: "preserve-3d" }}
      >
        <div className="relative overflow-hidden rounded-sm ring-1 ring-white/10 bg-[color:var(--charcoal)]">
          <img
            src={release.cover}
            alt={release.title}
            className="w-full aspect-square object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            loading="lazy"
          />
          {/* chrome sheen */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.12) 50%, transparent 70%)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full glass-strong flex items-center justify-center opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500">
              <Play size={18} className="text-white fill-white ml-0.5" />
            </div>
          </div>
        </div>

        <div className="flex items-baseline justify-between mt-4">
          <div>
            <h3 className="font-display steel-text text-xl md:text-2xl leading-none">{release.title}</h3>
            <p className="track-label text-white/40 mt-2">{release.featured}</p>
          </div>
          <span className="track-label text-white/30">{release.date}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Music() {
  const [active, setActive] = useState(null);

  return (
    <section id="music" className="relative w-full py-24 md:py-36 px-5 md:px-10" data-testid="section-music">
      <div className="max-w-[1400px] mx-auto">
        <SectionLabel index="01 / MUSIC" id="music-label">RELEASES</SectionLabel>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="track-label text-white/50 mb-4">LISTEN TO {BRAND.name}</p>
            <h2 className="font-display chrome-text text-5xl sm:text-6xl md:text-8xl leading-[0.9]">
              <Reveal text="LISTEN NOW" />
            </h2>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            <p className="font-body text-white/50 max-w-sm text-sm md:text-base md:text-right">
              Stream the world of {BRAND.name} on every platform.
            </p>
            <div className="flex flex-wrap gap-3">
              {LISTEN.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`listen-cta-${l.key}`}
                  data-cursor
                  className="flex items-center gap-2.5 px-6 py-3.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all duration-400 group"
                >
                  <BrandIcon name={l.icon} size={20} />
                  <span className="track-label">{l.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10 max-w-4xl mx-auto">
          {MUSIC.map((r, i) => (
            <TiltCard key={r.id} release={r} index={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[9600] flex items-center justify-center p-5 bg-black/80 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="music-modal"
          >
            <motion.div
              className="relative glass-strong rounded-sm max-w-3xl w-full grid sm:grid-cols-2 overflow-hidden"
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.cover} alt={active.title} className="w-full h-56 sm:h-full object-cover" />
              <div className="p-7 md:p-9 flex flex-col justify-center">
                <p className="track-label text-white/40 mb-3">{active.date} — {active.featured}</p>
                <h3 className="font-display chrome-text text-4xl md:text-5xl leading-none mb-6">{active.title}</h3>
                <p className="track-label text-white/50 mb-4">STREAM ON</p>
                <div className="flex flex-col gap-2">
                  {PLATFORMS.map((p) => (
                    <a
                      key={p.key}
                      href={active.links[p.key] || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`stream-${p.key}`}
                      className="flex items-center justify-between px-4 py-3 border border-white/12 hover:border-white hover:bg-white hover:text-black transition-all duration-300 group"
                    >
                      <span className="flex items-center gap-2.5 font-body text-sm text-white/80 group-hover:text-black">
                        <BrandIcon name={p.icon} size={18} /> {p.label}
                      </span>
                      <Play size={13} className="text-white/40 group-hover:text-black transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setActive(null)}
                data-testid="music-modal-close"
                className="absolute top-3 right-3 w-9 h-9 rounded-full glass flex items-center justify-center text-white/70 hover:text-white"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
